import type { ComponentProps, ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"

export type StatusKpiTone = "neutral" | "ok" | "warning" | "critical"

export type StatusKpiTileProps = ComponentProps<"div"> & {
  // Short uppercase eyebrow above the headline number, e.g.
  // "Services monitored". Kept short so the tile reads at a
  // glance and never wraps on the strip.
  label: string
  // Headline number rendered large. Strings (not numbers) so the
  // tile can render "1,247" or "338 ms" without each call site
  // reinventing the formatter.
  value: string
  // Single-line sub-text below the value. Empty strings render as
  // a non-breaking space so neighbouring tiles in the strip stay
  // aligned even when one tile has nothing extra to say.
  sub?: string
  // Optional decorative accent rendered to the right of the
  // label. We pass `ReactNode` rather than a baked-in icon set so
  // an inline SVG dot, badge, or even a tiny chart can sit in the
  // header strip without extending the tile API.
  accent?: ReactNode
  // Tone drives the headline-number colour and a faint left
  // border accent. Defaults to neutral (no accent) -- we only
  // tint a tile when there's a real signal to convey.
  tone?: StatusKpiTone
}

const TONE_VALUE: Record<StatusKpiTone, string> = {
  neutral: "text-foreground",
  ok: "text-emerald-300",
  warning: "text-amber-300",
  critical: "text-red-300",
}

const TONE_ACCENT_BORDER: Record<StatusKpiTone, string> = {
  neutral: "border-l-border/60",
  ok: "border-l-emerald-400/60",
  warning: "border-l-amber-400/60",
  critical: "border-l-red-400/60",
}

// One tile in the KPI strip at the top of the public page. Visual
// language is intentionally calmer than the previous outage banner:
// neutral background, small label, big number, single sub-line.
// Tone tints just the number and a thin left border so a customer
// can scan the strip and instantly see which tile is the one
// carrying news.
function StatusKpiTile(props: Readonly<StatusKpiTileProps>) {
  const {
    label,
    value,
    sub,
    accent,
    tone = "neutral",
    className,
    ...divProps
  } = props
  return (
    <div
      className={cn(
        "flex flex-col gap-1 rounded-lg border border-border/60 bg-card px-4 py-4 border-l-4",
        TONE_ACCENT_BORDER[tone],
        className,
      )}
      {...divProps}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        {accent}
      </div>
      <div
        className={cn(
          "text-3xl font-semibold leading-tight tracking-tight tabular-nums",
          TONE_VALUE[tone],
        )}
      >
        {value}
      </div>
      <div className="text-xs text-muted-foreground">{sub || "\u00a0"}</div>
    </div>
  )
}

export default StatusKpiTile
